import {
  parseRegridSpendGuardAddressIdentity,
  parseRegridSpendGuardStreetIdentity,
  regridAddressIdentitiesAgree,
} from "./regrid-address-identity.ts";

export const BERGEN_ORIGINAL_PROPERTY_SPEND_POLICY =
  "bergen_original_property_acceptance_v1" as const;

export const BERGEN_ORIGINAL_PROPERTY_ACCEPTANCE_ADDRESS =
  "45 Highland Place, Ridgefield Park, NJ 07660" as const;

export type ProviderSpendPolicy = typeof BERGEN_ORIGINAL_PROPERTY_SPEND_POLICY;

export const PROVIDER_SPEND_POLICY_SESSION_KEY =
  "fairpath_provider_spend_policy_v1";

export function isProviderSpendPolicy(value: unknown): value is ProviderSpendPolicy {
  return value === BERGEN_ORIGINAL_PROPERTY_SPEND_POLICY;
}

export type BrowserProviderSpendPolicyStatus = "inactive" | "active" | "invalid";

/**
 * A capped acceptance is sticky for the browser session. A malformed marker is
 * invalid, not permission to fall back to the normal paid path.
 */
export function browserProviderSpendPolicyStatus(): BrowserProviderSpendPolicyStatus {
  if (typeof window === "undefined") return "inactive";
  const values: unknown[] = [];
  const params = new URLSearchParams(window.location.search);
  if (params.has("provider_spend_policy")) {
    values.push(params.get("provider_spend_policy"));
  }
  try {
    const historyState = window.history.state;
    const routeState = historyState && typeof historyState === "object"
      ? (historyState as { usr?: unknown }).usr
      : null;
    if (routeState && typeof routeState === "object" &&
        Object.prototype.hasOwnProperty.call(routeState, "providerSpendPolicy")) {
      values.push((routeState as Record<string, unknown>).providerSpendPolicy);
    }
    if (routeState && typeof routeState === "object" &&
        Object.prototype.hasOwnProperty.call(routeState, "providerSpendPolicyInvalid")) {
      values.push(null);
    }
    const marker = window.sessionStorage.getItem(PROVIDER_SPEND_POLICY_SESSION_KEY);
    if (marker !== null) values.push(marker);
  } catch {
    // Route/query values already collected still determine a fail-closed mode.
  }
  if (values.length === 0) return "inactive";
  return values.every(isProviderSpendPolicy) ? "active" : "invalid";
}

/** UI suppression only; the server independently validates admin authority and the database cap. */
export function isBergenOriginalPropertySpendPolicyActiveInBrowser(): boolean {
  return browserProviderSpendPolicyStatus() !== "inactive";
}

/**
 * Broad match used to recognize requests that Regrid could resolve to the
 * protected parcel. It intentionally ignores locality text, as Regrid does,
 * so a changed city/state cannot turn a protected request into an unmetered
 * one. The stricter acceptance predicate below still validates locality.
 */
export function isBergenOriginalPropertyRegridEquivalentAddress(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const candidate = parseRegridSpendGuardAddressIdentity(value);
  const approved = parseRegridSpendGuardAddressIdentity(BERGEN_ORIGINAL_PROPERTY_ACCEPTANCE_ADDRESS);
  return candidate !== null && approved !== null &&
    regridAddressIdentitiesAgree(candidate, approved);
}

/**
 * Conservative guard for incomplete addresses. A missing ZIP cannot erase a
 * matching house/street, and caller-supplied locality text cannot make that
 * partial identity safe. Source-bound non-protected requests can still use a
 * validated cache-only read when their address is incomplete.
 */
export function isPotentialBergenOriginalPropertyAddress(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const complete = parseRegridSpendGuardAddressIdentity(value);
  if (complete) return isBergenOriginalPropertyRegridEquivalentAddress(value);

  const candidateStreet = parseRegridSpendGuardStreetIdentity(value);
  const approvedStreet = parseRegridSpendGuardStreetIdentity(BERGEN_ORIGINAL_PROPERTY_ACCEPTANCE_ADDRESS);
  if (!candidateStreet || !approvedStreet ||
      candidateStreet.houseNumber !== approvedStreet.houseNumber ||
      candidateStreet.streetKey !== approvedStreet.streetKey) {
    return false;
  }

  return true;
}

function normalizeLocality(value: string): string {
  return value.normalize("NFKD").toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ").trim().replace(/\s+/g, " ");
}

function hasUnitDesignation(value: string): boolean {
  const normalized = value.normalize("NFKD");
  return normalized.includes("#") ||
    /(?:^|[^A-Z0-9])(?:APT|APARTMENT|UNIT|SUITE|STE)(?=$|[^A-Z])/i.test(normalized);
}

/**
 * Exact authorized subject binding. House/street/ZIP uses Regrid's canonical
 * identity, while city/state and unit are additional non-widening constraints
 * so formatting aliases do not authorize another locality or residence.
 */
export function isBergenOriginalPropertyAcceptanceAddress(value: unknown): value is string {
  if (typeof value !== "string" || !isBergenOriginalPropertyRegridEquivalentAddress(value)) {
    return false;
  }
  const parts = value.split(",").map((part) => part.trim());
  if (parts.length !== 3 || parts.some(hasUnitDesignation)) return false;
  const stateAndZip = parts[2].match(/^([A-Z]{2})\s+(\d{5})(?:-\d{4})?$/i);
  return stateAndZip !== null &&
    normalizeLocality(parts[1]) === "RIDGEFIELD PARK" &&
    stateAndZip[1].toUpperCase() === "NJ" &&
    stateAndZip[2] === "07660";
}
